import React from "react";
import { IconButton } from "../core/IconButton.jsx";

/* Modal dialog — backdrop + centred card. open/onClose controlled. */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 520,
  closeOnBackdropClick = true,
  style,
  ...rest
}) {
  React.useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose && onClose(); }
    // Escape is a casual dismissal gesture like the backdrop click, so the same opt-out governs both —
    // a data-entry form (closeOnBackdropClick={false}) is protected from accidental Esc discards too.
    if (open && closeOnBackdropClick) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, closeOnBackdropClick]);

  if (!open) return null;

  const showHeader = Boolean(title || subtitle || onClose);

  return (
    <div
      onClick={closeOnBackdropClick ? onClose : undefined}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(24,28,36,0.45)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "tk-fade var(--dur) var(--ease)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: width,
          maxHeight: "calc(100vh - 48px)",
          overflow: "auto",
          background: "var(--surface-card)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-4)",
          animation: "tk-pop var(--dur-slow) var(--ease)",
          ...style,
        }}
        {...rest}
      >
        {showHeader ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "22px 24px 0" }}>
            <div>
              {title ? <h2 style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 20, letterSpacing: "-0.01em", color: "var(--text-strong)", margin: 0 }}>{title}</h2> : null}
              {subtitle ? <div style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 14, color: "var(--text-muted)", marginTop: 4 }}>{subtitle}</div> : null}
            </div>
            {onClose ? <IconButton icon="x" label="Close" onClick={onClose} /> : null}
          </div>
        ) : null}
        <div style={{ padding: showHeader ? "18px 24px 4px" : "22px 24px 4px" }}>{children}</div>
        {footer ? (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 24px 22px" }}>{footer}</div>
        ) : <div style={{ height: 18 }} />}
      </div>
      <style>{`
        @keyframes tk-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes tk-pop { from { opacity: 0; transform: translateY(8px) scale(0.98) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) { [role="dialog"], [role="dialog"]+style { animation: none !important } }
      `}</style>
    </div>
  );
}

export default Modal;
